/**
 * Declare a mounted component's feed-filter needs for as long as it is
 * mounted (consumer feed filters, Stage C). Mirrors the `setOrderbookNeeds`
 * effect every view already carries.
 *
 *   useFeedNeeds('game-lines:mlb', isMlb ? { classes: ['player-prop'] } : null);
 *   useFeedNeeds('value-dashboard', { sports: allSports ? 'all' : selected });
 *
 * `needs` is compared by value (a stable key), so callers may pass fresh
 * object literals every render without re-publishing. Passing `null` clears
 * the declaration (same as unmounting).
 */
import { useEffect } from 'react';
import { declareFeedNeeds } from '../stores/feedNeeds';
import type { FeedNeeds } from '../workers/feedFilter';

function needsKey(needs: FeedNeeds | null): string {
  if (needs == null) return '';
  const list = (v?: Iterable<string>): string[] => (v ? [...v].sort() : []);
  return JSON.stringify({
    c: list(needs.classes),
    t: list(needs.marketTypes),
    s: needs.sports === undefined || needs.sports === 'all' ? '*' : list(needs.sports),
  });
}

export function useFeedNeeds(sourceId: string, needs: FeedNeeds | null): void {
  const key = needsKey(needs);
  useEffect(() => {
    declareFeedNeeds(sourceId, needs);
    return () => declareFeedNeeds(sourceId, null);
    // `needs` is folded into `key` by value; the object identity is irrelevant.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, key]);
}
